import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';
import {
  ArrowLeft,
  Image,
  Film,
  Mic,
  Eye,
  Play,
  BarChart3,
  X,
  Trophy,
  Medal,
  Award,
  Users,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';

interface UserStats {
  user: {
    _id: string;
    name: string;
    email?: string;
  };
  count: number;
}

interface MostViewedPost {
  _id: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  originalFilename?: string;
  processedFilename?: string;
  viewCount: number;
  user: {
    _id: string;
    name: string;
    email?: string;
  };
  createdAt: string;
}

interface SummaryStats {
  totalPosts: number;
  totalViews: number;
  totalUsers: number;
  totalPhotos: number;
  totalVideos: number;
  totalVoiceNotes: number;
}

interface StatsData {
  summary: SummaryStats;
  mostPhotos: UserStats[];
  mostVideos: UserStats[];
  mostVoiceNotes: UserStats[];
  mostViewed: MostViewedPost[];
  mostActive: UserStats[];
}

interface MediaPreview {
  type: 'photo' | 'video';
  url: string;
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Image,
  voiceNote: Mic,
};

// Helper function to format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Helper function to get medal styling for top 3 positions
const getMedalStyle = (position: number): { icon: typeof Trophy | typeof Medal | typeof Award; gradient: string; size: string } => {
  if (position === 0) {
    return {
      icon: Trophy,
      gradient: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
      size: 'w-8 h-8',
    };
  }
  if (position === 1) {
    return {
      icon: Medal,
      gradient: 'bg-gradient-to-br from-gray-300 to-gray-500',
      size: 'w-7 h-7',
    };
  }
  if (position === 2) {
    return {
      icon: Medal,
      gradient: 'bg-gradient-to-br from-orange-400 to-orange-600',
      size: 'w-7 h-7',
    };
  }
  return {
    icon: Award,
    gradient: 'bg-muted',
    size: 'w-6 h-6',
  };
};

export default function StatsPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, authenticatedFetch } = useAuth();
  
  const [stats, setStats] = useState<StatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const [replayingPostId, setReplayingPostId] = useState<string | null>(null);
  
  // Check authentication - redirect to login if not authenticated with Google as admin
  useEffect(() => {
    if (!authLoading && (!user || user.authType !== 'google' || user.role !== 'admin')) {
      navigate('/login?redirect=/stats');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await authenticatedFetch(`${baseUrl}/api/stats`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load stats',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplay = async (postId: string) => {
    try {
      setReplayingPostId(postId);
      const baseUrl = getApiBaseUrl();
      const response = await authenticatedFetch(`${baseUrl}/api/posts/${postId}/replay`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to replay post');
      }
      
      toast({
        title: 'Success',
        description: 'Post replayed successfully',
      });
      
      // Refresh stats to update view counts
      fetchStats();
    } catch (error) {
      console.error('Failed to replay post:', error);
      toast({
        title: 'Error',
        description: 'Failed to replay post',
        variant: 'destructive',
      });
    } finally {
      setReplayingPostId(null);
    }
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    return `${getApiBaseUrl()}/uploads/${filename}`;
  };

  const handlePostClick = (post: MostViewedPost) => {
    if (post.contentType === 'photo' || post.contentType === 'video') {
      const mediaUrl = getMediaUrl(post.processedFilename || post.originalFilename);
      if (mediaUrl) {
        setMediaPreview({
          type: post.contentType,
          url: mediaUrl,
        });
      }
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  // Don't render if not authenticated as admin (redirect will happen)
  if (!user || user.authType !== 'google' || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">No stats available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
          <Link to="/share">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
          <h1 className="font-display font-semibold text-lg lg:text-xl flex-1">Stats Dashboard</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-6 lg:space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
          <div className="rounded-xl border border-border bg-card p-5 lg:p-6 bg-gradient-to-br from-primary/10 to-primary/5">
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold">{formatNumber(stats.summary.totalPosts)}</div>
            <div className="text-sm lg:text-base text-muted-foreground">Total Posts</div>
          </div>
          
          <div className="rounded-xl border border-border bg-card p-5 lg:p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5">
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <Eye className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold">{formatNumber(stats.summary.totalViews)}</div>
            <div className="text-sm lg:text-base text-muted-foreground">Total Views</div>
          </div>
          
          <div className="rounded-xl border border-border bg-card p-5 lg:p-6 bg-gradient-to-br from-green-500/10 to-green-500/5">
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-green-500" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold">{formatNumber(stats.summary.totalUsers)}</div>
            <div className="text-sm lg:text-base text-muted-foreground">Total Users</div>
          </div>
          
          <div className="rounded-xl border border-border bg-card p-5 lg:p-6 bg-gradient-to-br from-purple-500/10 to-purple-500/5">
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6 text-purple-500" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold">
              {stats.summary.totalPhotos + stats.summary.totalVideos + stats.summary.totalVoiceNotes}
            </div>
            <div className="text-sm lg:text-base text-muted-foreground">Total Content</div>
          </div>
        </div>

        {/* Leaderboards Grid - Desktop: 2 columns, Mobile: 1 column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Most Photos Leaderboard */}
          <section className="rounded-xl border border-border bg-card p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-semibold mb-6 flex items-center gap-2">
              <Image className="w-5 h-5 lg:w-6 lg:h-6" />
              Most Photos
            </h2>
            <div className="space-y-3">
            {stats.mostPhotos.length === 0 ? (
              <p className="text-muted-foreground text-sm">No photos yet</p>
            ) : (
              stats.mostPhotos.map((item, index) => {
                const medalStyle = getMedalStyle(index);
                const MedalIcon = medalStyle.icon;
                const isTopThree = index < 3;
                
                return (
                  <div
                    key={item.user._id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md ${
                      isTopThree
                        ? `${medalStyle.gradient} text-white border-2 border-white/20`
                        : 'bg-muted/50 border border-border'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex items-center justify-center ${medalStyle.size} ${isTopThree ? '' : 'text-muted-foreground'}`}>
                        {isTopThree ? (
                          <MedalIcon className={medalStyle.size} />
                        ) : (
                          <span className="font-bold text-lg">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${isTopThree ? 'text-white' : ''}`}>
                          {item.user.name}
                        </div>
                        {item.user.email && (
                          <div className={`text-xs ${isTopThree ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {item.user.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`text-xl font-bold ${isTopThree ? 'text-white' : ''}`}>
                      {item.count}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </section>

          {/* Most Videos Leaderboard */}
          <section className="rounded-xl border border-border bg-card p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-semibold mb-6 flex items-center gap-2">
              <Film className="w-5 h-5 lg:w-6 lg:h-6" />
              Most Videos
            </h2>
            <div className="space-y-3">
            {stats.mostVideos.length === 0 ? (
              <p className="text-muted-foreground text-sm">No videos yet</p>
            ) : (
              stats.mostVideos.map((item, index) => {
                const medalStyle = getMedalStyle(index);
                const MedalIcon = medalStyle.icon;
                const isTopThree = index < 3;
                
                return (
                  <div
                    key={item.user._id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md ${
                      isTopThree
                        ? `${medalStyle.gradient} text-white border-2 border-white/20`
                        : 'bg-muted/50 border border-border'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex items-center justify-center ${medalStyle.size} ${isTopThree ? '' : 'text-muted-foreground'}`}>
                        {isTopThree ? (
                          <MedalIcon className={medalStyle.size} />
                        ) : (
                          <span className="font-bold text-lg">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${isTopThree ? 'text-white' : ''}`}>
                          {item.user.name}
                        </div>
                        {item.user.email && (
                          <div className={`text-xs ${isTopThree ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {item.user.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`text-xl font-bold ${isTopThree ? 'text-white' : ''}`}>
                      {item.count}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </section>

          {/* Most Voice Notes Leaderboard */}
          <section className="rounded-xl border border-border bg-card p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-semibold mb-6 flex items-center gap-2">
              <Mic className="w-5 h-5 lg:w-6 lg:h-6" />
              Most Voice Notes
            </h2>
            <div className="space-y-3">
            {stats.mostVoiceNotes.length === 0 ? (
              <p className="text-muted-foreground text-sm">No voice notes yet</p>
            ) : (
              stats.mostVoiceNotes.map((item, index) => {
                const medalStyle = getMedalStyle(index);
                const MedalIcon = medalStyle.icon;
                const isTopThree = index < 3;
                
                return (
                  <div
                    key={item.user._id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md ${
                      isTopThree
                        ? `${medalStyle.gradient} text-white border-2 border-white/20`
                        : 'bg-muted/50 border border-border'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex items-center justify-center ${medalStyle.size} ${isTopThree ? '' : 'text-muted-foreground'}`}>
                        {isTopThree ? (
                          <MedalIcon className={medalStyle.size} />
                        ) : (
                          <span className="font-bold text-lg">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${isTopThree ? 'text-white' : ''}`}>
                          {item.user.name}
                        </div>
                        {item.user.email && (
                          <div className={`text-xs ${isTopThree ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {item.user.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`text-xl font-bold ${isTopThree ? 'text-white' : ''}`}>
                      {item.count}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </section>

          {/* Most Active Leaderboard */}
          <section className="rounded-xl border border-border bg-card p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-semibold mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6" />
              Most Active
            </h2>
            <div className="space-y-3">
              {stats.mostActive.length === 0 ? (
                <p className="text-muted-foreground text-sm">No activity yet</p>
              ) : (
                stats.mostActive.map((item, index) => {
                  const medalStyle = getMedalStyle(index);
                  const MedalIcon = medalStyle.icon;
                  const isTopThree = index < 3;
                  
                  return (
                    <div
                      key={item.user._id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md ${
                        isTopThree
                          ? `${medalStyle.gradient} text-white border-2 border-white/20`
                          : 'bg-muted/50 border border-border'
                      }`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`flex items-center justify-center ${medalStyle.size} ${isTopThree ? '' : 'text-muted-foreground'}`}>
                          {isTopThree ? (
                            <MedalIcon className={medalStyle.size} />
                          ) : (
                            <span className="font-bold text-lg">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold ${isTopThree ? 'text-white' : ''}`}>
                            {item.user.name}
                          </div>
                          {item.user.email && (
                            <div className={`text-xs ${isTopThree ? 'text-white/80' : 'text-muted-foreground'}`}>
                              {item.user.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-xl font-bold ${isTopThree ? 'text-white' : ''}`}>
                        {item.count} posts
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        {/* Most Viewed Content - Full Width */}
        <section className="rounded-xl border border-border bg-card p-6 lg:p-8">
          <h2 className="text-xl lg:text-2xl font-semibold mb-6 flex items-center gap-2">
            <Eye className="w-5 h-5 lg:w-6 lg:h-6" />
            Most Viewed Content
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {stats.mostViewed.length === 0 ? (
              <p className="text-muted-foreground text-sm">No views yet</p>
            ) : (
              stats.mostViewed.map((post, index) => {
                const Icon = contentTypeIcons[post.contentType];
                const mediaUrl = getMediaUrl(post.processedFilename || post.originalFilename);
                const canPreview = post.contentType === 'photo' || post.contentType === 'video';
                const canReplay = post.contentType === 'photo' || post.contentType === 'video' || post.contentType === 'voiceNote';
                const medalStyle = getMedalStyle(index);
                const MedalIcon = medalStyle.icon;
                const isTopThree = index < 3;
                
                return (
                  <div
                    key={post._id}
                    className={`rounded-lg border-2 p-5 lg:p-6 transition-all hover:shadow-lg flex flex-col ${
                      isTopThree
                        ? `${medalStyle.gradient} text-white border-white/30`
                        : 'border-border bg-muted/50'
                    } ${canPreview ? 'cursor-pointer' : ''}`}
                    onClick={() => canPreview && handlePostClick(post)}
                  >
                    {/* Medal/Position - Centered at top */}
                    <div className="flex items-center justify-center mb-4">
                      <div className={`flex items-center justify-center ${medalStyle.size} ${isTopThree ? '' : 'text-muted-foreground'}`}>
                        {isTopThree ? (
                          <MedalIcon className={medalStyle.size} />
                        ) : (
                          <span className="font-bold text-lg">{index + 1}</span>
                        )}
                      </div>
                    </div>

                    {/* Header Info - Centered */}
                    <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                      <Icon className={`w-4 h-4 ${isTopThree ? 'text-white' : 'text-muted-foreground'}`} />
                      <span className={`text-sm font-semibold ${isTopThree ? 'text-white' : ''}`}>
                        {post.user.name}
                      </span>
                      <span className={`text-xs ${isTopThree ? 'text-white/80' : 'text-muted-foreground'}`}>
                        {formatDate(post.createdAt)}
                      </span>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                        isTopThree ? 'bg-white/20' : 'bg-primary/10'
                      }`}>
                        <Eye className={`w-3 h-3 ${isTopThree ? 'text-white' : 'text-primary'}`} />
                        <span className={`text-xs font-semibold ${isTopThree ? 'text-white' : 'text-primary'}`}>
                          {formatNumber(post.viewCount)}
                        </span>
                      </div>
                    </div>

                    {/* Caption - Centered */}
                    {post.caption && (
                      <p className={`text-sm mb-3 text-center line-clamp-2 ${isTopThree ? 'text-white/90' : 'text-muted-foreground'}`}>
                        {post.caption}
                      </p>
                    )}

                    {/* Media - Centered */}
                    {canPreview && mediaUrl && (
                      <div className="mt-3 rounded-lg overflow-hidden bg-black/30 flex items-center justify-center">
                        {post.contentType === 'photo' && (
                          <img
                            src={mediaUrl}
                            alt=""
                            className="w-full max-h-48 lg:max-h-64 object-contain"
                          />
                        )}
                        {post.contentType === 'video' && (
                          <video
                            src={mediaUrl}
                            className="w-full max-h-48 lg:max-h-64"
                            controls={false}
                          />
                        )}
                      </div>
                    )}

                    {/* Replay Button - Centered */}
                    {canReplay && (
                      <div className="mt-4 flex justify-center">
                        <Button
                          variant={isTopThree ? 'secondary' : 'outline'}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplay(post._id);
                          }}
                          disabled={replayingPostId === post._id}
                          className={isTopThree ? 'bg-white/20 hover:bg-white/30 text-white border-white/30' : ''}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {replayingPostId === post._id ? 'Replaying...' : 'Replay'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>

      {/* Media Preview Dialog */}
      <Dialog open={!!mediaPreview} onOpenChange={() => setMediaPreview(null)}>
        <DialogContent className="fixed inset-4 max-w-none left-0 top-0 flex items-center justify-center p-0 bg-black/95 border-none rounded-xl data-[state=open]:dialog-fullscreen-animate-in data-[state=closed]:dialog-fullscreen-animate-out">
          <button
            onClick={() => setMediaPreview(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {mediaPreview?.type === 'photo' && (
            <img
              src={mediaPreview.url}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          )}
          {mediaPreview?.type === 'video' && (
            <video
              src={mediaPreview.url}
              className="max-w-full max-h-full"
              controls
              autoPlay
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


